/**
 * TypeScript type definitions for the Scheduled Smiles application.
 * These types mirror the backend API models for type-safe frontend development.
 */

export type { PublicSubscriptionConfig } from './publicSubscriptionConfig';
export type {
  PublicMerchantDeliveryTime,
  PublicMerchantListingPolicy,
  PublicMerchantReturnPolicy,
} from './merchantListingPolicy';

import type { CatalogTagAssignment } from '../utils/catalogTagFilters';

/**
 * Recurrence type enumeration matching backend RecurrenceType enum.
 * Defines how an event type recurs over time.
 */
export enum RecurrenceType {
  None = 'None',
  Yearly = 'Yearly',
  EveryNYears = 'EveryNYears',
}

/**
 * Determines how an event type resolves its date.
 * Matches backend DateSourceType enum.
 */
export enum DateSourceType {
  ContactDate = 'ContactDate',
  FixedDate = 'FixedDate',
  FloatingHoliday = 'FloatingHoliday',
}

/**
 * Calculation strategy for floating holidays.
 * Matches backend FloatingHolidayRule enum.
 */
export enum FloatingHolidayRule {
  NthWeekdayOfMonth = 'NthWeekdayOfMonth',
  LastWeekdayOfMonth = 'LastWeekdayOfMonth',
  EasterOffset = 'EasterOffset',
}

/**
 * Scheduled item status enumeration matching backend ScheduledItemStatus enum.
 */
export enum ScheduledItemStatus {
  Pending = 'Pending',
  Sent = 'Sent',
  Failed = 'Failed',
  Cancelled = 'Cancelled',
  /** Physical order placed with vendor (e.g. Florist One) but not yet delivered. */
  OrderPlaced = 'OrderPlaced',
}

/**
 * Scheduled card status enumeration matching backend ScheduledCardStatus enum.
 */
export enum ScheduledCardStatus {
  Pending = 'Pending',
  Sent = 'Sent',
  Failed = 'Failed',
  Cancelled = 'Cancelled',
}

/**
 * Time slot enumeration for scheduling when items should be sent.
 * Each time slot represents a window during which the item will be processed.
 * 
 * Time windows (UTC):
 * - EarlyMorning: 05:00 - 08:00 (processed after 08:00)
 * - Morning: 08:00 - 12:00 (processed after 12:00) - Default
 * - Afternoon: 12:00 - 17:00 (processed after 17:00)
 * - Evening: 17:00 - 21:00 (processed after 21:00)
 */
export enum TimeSlot {
  EarlyMorning = 'EarlyMorning',
  Morning = 'Morning',
  Afternoon = 'Afternoon',
  Evening = 'Evening',
}

/**
 * Scheduled action type enumeration for extensible action support.
 */
export enum ScheduledActionType {
  Email = 'Email',
  FlowerOrder = 'FlowerOrder',
  GiftOrder = 'GiftOrder',
  MailedCardOrder = 'MailedCardOrder',
}

/**
 * Event type entity representing a category of recurring events.
 * EventTypes define the scheduling behavior and are independent of contacts.
 */
export interface EventType {
  id: number;
  /** Globally unique identifier for cross-environment content sync. */
  syncId?: string;
  /** Stable catalog slug identifier (e.g. EVT-001). */
  slug: string;
  userId?: string;
  name: string;
  description?: string;
  recurrenceType: RecurrenceType;
  recurrenceInterval: number;
  dateFieldName: string;
  isSystemDefined: boolean;
  /** UI color for this event type (Mantine color name, e.g., "pink", "teal", "red") */
  color: string;
  /** Month (1-12) for fixed-date holidays that occur on the same calendar date each year. */
  fixedMonth?: number;
  /** Day (1-31) for fixed-date holidays that occur on the same calendar date each year. */
  fixedDay?: number;
  /** Discriminator: ContactDate, FixedDate, or FloatingHoliday */
  dateSourceType: DateSourceType;
  /** Calculation strategy for floating holidays */
  floatingHolidayRule?: FloatingHolidayRule;
  /** Month (1-12) for NthWeekdayOfMonth / LastWeekdayOfMonth rules */
  floatingRuleMonth?: number;
  /** Day of week (0=Sunday .. 6=Saturday) */
  floatingRuleWeekday?: number;
  /** Which occurrence (1-5) for NthWeekdayOfMonth */
  floatingRuleOrdinal?: number;
  /** Days offset from Easter Sunday for EasterOffset rule */
  floatingRuleEasterOffset?: number;
  /** Region label for display, e.g., "US" */
  holidayRegionLabel?: string;
  /** Whether this event type is eligible for flower delivery add-ons. */
  isEligibleForFlowers?: boolean;
  /** Florist One category codes (e.g. ["bd", "an"]) mapped to this event type. */
  floristOneCategoryCodes?: string[];
  /** Whether this event type has been archived (soft deleted). */
  isArchived?: boolean;
  /** Number of card templates linked to this event type. Used to determine if deletion is blocked. */
  templateCount?: number;
  /** Number of scheduled items linked to this event type. Used to determine if deletion is blocked. */
  scheduledItemCount?: number;
  /** URL to the background illustration image, served via /api/media/{token}. Null when no background. */
  backgroundUrl?: string | null;
  /** CSS animation preset for the background illustration: "float", "flicker", "pulse", "twinkle", "sway", "fall", or null. */
  animationPreset?: string | null;
  /** Whether the seasonal marketing banner is enabled for this holiday event type. */
  seasonalBannerEnabled?: boolean;
  /** Days before the event date the seasonal banner may appear (1-60). */
  seasonalBannerLeadDays?: number;
  /** Overlay headline for the seasonal banner. */
  seasonalBannerHeadline?: string | null;
  /** Optional supporting subtext for the seasonal banner. */
  seasonalBannerSubtext?: string | null;
  /** Relative media URL for the seasonal banner image when uploaded. */
  seasonalBannerUrl?: string | null;
}

/**
 * Custom date for a contact (e.g., Wedding Anniversary, Hire Date).
 */
export interface ContactCustomDate {
  id: number;
  name: string;
  anniversaryStartDate: string; // ISO date string
  /** When false, only month/day are meaningful; stored year is a sentinel. */
  anniversaryStartYearKnown?: boolean;
}

/**
 * Controls how a contact-event subscription handles upcoming events.
 * None = tracked only, Notify = send reminders, AutoSend = auto-schedule cards.
 */
export enum ScheduleMode {
  None = 'None',
  Notify = 'Notify',
  AutoSend = 'AutoSend',
}

/**
 * Type of service that can be enrolled for a contact-event subscription.
 * Each service type has its own scheduling mode and action flow.
 */
export enum ServiceType {
  ECard = 'ECard',
  Flowers = 'Flowers',
  MailedCard = 'MailedCard',
  Gift = 'Gift',
}

/**
 * Budget tiers for AutoSend flower selection.
 * Matches backend FlowerBudgetRange enum.
 */
export enum FlowerBudgetRange {
  Budget = 'Budget',
  Standard = 'Standard',
  Premium = 'Premium',
}

/**
 * Shared vendor fulfillment lifecycle for paid orders (flowers, mailed cards).
 * Matches backend `FulfillmentStatus` enum (PBI 1011).
 */
export enum FulfillmentStatus {
  Pending = 'Pending',
  Placed = 'Placed',
  Shipped = 'Shipped',
  Delivered = 'Delivered',
  Failed = 'Failed',
  Cancelled = 'Cancelled',
  Undeliverable = 'Undeliverable',
}

/**
 * Shared Stripe payment lifecycle for paid orders (flowers, mailed cards).
 * Matches backend `PaymentStatus` enum (PBI 1011).
 */
export enum PaymentStatus {
  None = 'None',
  Authorized = 'Authorized',
  Captured = 'Captured',
  CaptureFailed = 'CaptureFailed',
  Voided = 'Voided',
  RefundPending = 'RefundPending',
  Refunded = 'Refunded',
  PartiallyRefunded = 'PartiallyRefunded',
  AuthorizationFailed = 'AuthorizationFailed',
}

/**
 * Per-service enrollment within a contact-event subscription.
 * Each service tracks its own independent scheduling mode.
 */
export interface ContactEventTypeService {
  serviceType: ServiceType;
  scheduleMode: ScheduleMode;
  flowerBudgetRange?: FlowerBudgetRange | null;
  /** Default special delivery instructions for flower orders (max 100 chars). */
  flowerSpecialInstructions?: string | null;
  /** Default substitution preference for flower orders. Defaults to true. */
  flowerAllowSubstitutions: boolean;
  /** Optional AI fallback message for mailed-card Auto Send. */
  mailedCardDefaultMessage?: string | null;
  /** Default shipping method for mailed-card Auto Send. */
  mailedCardShippingMethod?: string | null;
}

/**
 * Contact event type subscription.
 * Scheduling mode has moved to per-service level via ContactEventTypeService.
 */
export interface ContactEventType {
  eventTypeId: number;
  eventTypeName: string;
  /** User-authored AI guidance for greeting text generation on this contact+event. */
  aiGuidance?: string;
  /** Per-event Tone override; falls back to Contact.preferredTone when null. */
  preferredTone?: string | null;
  /** Hard gate for whether Contact.notes flow into the AI prompt for this event. */
  includeNotesInAiPrompt?: boolean;
  /** Service enrollments for this subscription, each with its own scheduling mode. */
  services: ContactEventTypeService[];
}

/**
 * Contact model representing a person that receives greeting cards.
 */
export interface Contact {
  id: number;
  userId: string;
  firstName: string;
  lastName: string;
  email?: string;
  phone?: string;
  notes?: string;
  birthday?: string; // ISO date string
  /** When false, only month/day are meaningful; stored year is a sentinel. */
  birthdayYearKnown?: boolean;
  /** Street address line 1. */
  addressLine1?: string;
  /** Street address line 2 (apartment, suite, etc.). */
  addressLine2?: string;
  /** City name. */
  city?: string;
  /** State or province. */
  state?: string;
  /** Postal / ZIP code. */
  postalCode?: string;
  /** Country name. */
  country?: string;
  /** Whether the contact is locked due to subscription lapse */
  isLocked?: boolean;
  customDates?: ContactCustomDate[];
  eventTypes?: ContactEventType[];
  /** Contact's relationship to the user (e.g., "Friend", "Colleague"). */
  relationship?: string;
  /** Preferred card tone (e.g., "Professional", "Funny", "Teasing"). */
  preferredTone?: string;
  /** Preferred media type: "Image" or "Animated". */
  preferredMediaType?: string;
  /** Preferred visual art style (e.g., "Photorealistic", "Watercolor"). */
  preferredVisualStyle?: string;
  /** Default AI writing instructions for this contact (e.g., "Keep it short, no work talk"). */
  aiInstructions?: string;
  /** Import batch that created this contact, when applicable. */
  importBatchId?: string;
  /** UTC time of the import batch that created this contact. */
  importedUtc?: string;
}

/**
 * Event type row linked to a card template (many-to-many from the API).
 * Full {@link EventType} may be supplied for user templates; admin/public surfaces
 * may return only id + slug + name.
 */
export interface CardTemplateEventTypeRef {
  id: number;
  /** Stable catalog slug (e.g. EVT-001). */
  slug: string;
  name: string;
}

/**
 * Card template model representing reusable card designs.
 * Templates may map to multiple catalog event types (scheduling picks a concrete type per send).
 */
export interface CardTemplate {
  id: number;
  /** Globally unique identifier for cross-environment content sync. */
  syncId?: string;
  /** Stable catalog slug identifier (e.g. TPL-001). */
  slug: string;
  /**
   * Mapped event types for this template. When absent or empty, treat as not schedulable
   * until the API returns populated mappings.
   */
  eventTypes?: CardTemplateEventTypeRef[];
  name: string;
  /** Human-readable description for alt text and landing page display (max 160 chars) */
  description: string;
  defaultGreetingText: string;
  /**
   * Default email subject line for cards using this template.
   * Supports placeholders: {{RECIPIENT_NAME}}, {{RECIPIENT_FIRST_NAME}}, {{SENDER_NAME}}
   * When null/empty, falls back to default: "{SenderName} sent you a {EventType} greeting!"
   */
  defaultSubjectLine?: string;
  isAnimated: boolean;
  previewImageUrl?: string;
  isSystemTemplate: boolean;
  ownerId?: string;
  /** Token for accessing uploaded media via /api/media/{mediaToken} */
  mediaToken?: string;
  /** Type of uploaded media: "image" or "video" */
  mediaType?: string;
  /**
   * Non-destructive 16:9 frame (PBI #1161). When set, product surfaces fill the
   * media slot edge-to-edge; when absent, letterbox with object-fit contain.
   */
  mediaFrame?: import('./mediaFrame').MediaFrame | null;
  /** Alias for API camelCase MediaFrameJson deserialized as mediaFrame */
  mediaFrameJson?: string | null;
  /** Video poster JPEG token (same MediaController route) when mediaType is video */
  posterMediaToken?: string;
  /**
   * Optional responsive WebP thumbnail tokens (240 / 480 / 720) for carousel tiles.
   * Any non-empty subset may be present; tile surfaces emit an {@code <img srcSet>} for
   * whichever widths exist, with the original media URL as {@code src} fallback.
   */
  thumbnailTokens?: {
    w240?: string;
    w480?: string;
    w720?: string;
  };
  /** ID of the linked AI content generation session, if any */
  generationSessionId?: string;
  /** Self-referencing link to a paired template (still image ↔ video) */
  linkedTemplateId?: number;
  /** Card tone (e.g., "Professional", "Funny", "Teasing"). */
  tone?: string;
  /** Primary persona (e.g., "Friend", "Universal"). */
  primaryPersona?: string;
  /** Visual art style name (e.g., "Photorealistic", "Watercolor"). */
  visualStyleName?: string;
  /** Catalog tag assignments (TAG-NNN per EVT-NNN) for gallery chip filtering. */
  tagAssignments?: CatalogTagAssignment[];
}

/**
 * Response from POST /api/admin/media/backfill-thumbnails (admin carousel thumbnail catch-up).
 * Matches {@code BackfillThumbnailsResponseDto} on the API.
 */
export interface BackfillThumbnailsResponse {
  dryRun: boolean;
  page: number;
  pageSize: number;
  templatesMissingCount: number;
  scheduledCardsMissingCount: number;
  templatesProcessed: number;
  scheduledCardsProcessed: number;
  templatesSucceeded: number;
  scheduledCardsSucceeded: number;
  completed: boolean;
  reencodeLossySystemTemplates?: boolean;
  systemTemplatesReencodeCandidateCount?: number;
  templatesReencoded?: number;
  templatesSkippedAlreadyLossy?: number;
}

/**
 * Nested event-type row returned on admin template list/detail (AdminCardTemplateEventTypeDto).
 */
export interface AdminCardTemplateEventTypeRow {
  id: number;
  syncId?: string;
  slug: string;
  name: string;
  description?: string | null;
  color: string;
  /** Junction SortOrder for this template under this event type (admin drag-drop). */
  sortOrder: number;
}

/**
 * One accordion section on the admin system-templates page (templates mapped to an event type).
 */
export interface AdminTemplateGroup {
  eventTypeId: number;
  /** EVT-NNN slug; used for reorder API. Synthetic "(No mappings)" groups use a sentinel slug. */
  eventTypeSlug: string;
  eventTypeName: string;
  eventTypeDescription: string | null;
  eventTypeColor: string;
  templates: AdminCardTemplateListItem[];
  activeCount: number;
  archivedCount: number;
}

/**
 * Admin system template row from the lean paged list or full detail GET (PBI #1170).
 * List responses omit greeting/creative fields; detail GET fills them for edit/preview.
 */
export interface AdminCardTemplateListItem {
  id: number;
  syncId?: string;
  slug: string;
  name: string;
  description: string;
  eventTypes: AdminCardTemplateEventTypeRow[];
  /** TAG-NNN slugs assigned to this template (admin). */
  tagSlugs?: string[];
  /** Display names for catalog tags; aligned with {@link tagSlugs} indices. */
  tagNames?: string[];
  /** Present on detail GET; omitted from lean list pages. */
  defaultGreetingText?: string;
  defaultSubjectLine?: string | null;
  isAnimated: boolean;
  isSystemTemplate: boolean;
  isArchived: boolean;
  archivedAtUtc: string | null;
  mediaToken?: string | null;
  mediaType?: string | null;
  /** Non-destructive 16:9 frame for custom media (PBI #1161). */
  mediaFrame?: import('./mediaFrame').MediaFrame | null;
  /** Video poster still token when mediaType is video. */
  posterMediaToken?: string | null;
  /** Responsive WebP thumbs for admin list hover (prefer over full mediaToken). */
  thumbnailTokens?: {
    w240?: string | null;
    w480?: string | null;
    w720?: string | null;
  } | null;
  scheduledCardCount: number;
  previewImageUrl?: string | null;
  generationSessionId?: string | null;
  linkedTemplateId?: number | null;
  tone?: string | null;
  primaryPersona?: string | null;
  visualStyleName?: string | null;
  /** True when this system template has a row on the public landing-page carousel. */
  isOnLandingCarousel?: boolean;
}

/**
 * Response from uploading media for a template.
 */
export interface UploadMediaResponse {
  /** URL to access the uploaded media via API */
  mediaUrl: string;
  /** Type of media: "image" or "video" */
  mediaType: string;
  /** Frame persisted with the upload when provided (PBI #1161) */
  mediaFrame?: import('./mediaFrame').MediaFrame | null;
}

/**
 * Scheduled item model tracking what needs to be sent and when.
 * Links a contact and event type to a single scheduled card (1:1 relationship).
 */
export interface ScheduledItem {
  id: number;
  contactId: number;
  contact?: Contact;
  eventTypeId: number;
  eventType?: EventType;
  userId: string;
  /** Date when the item should be sent (time is determined by timeSlot) */
  scheduledDate: string; // ISO date string
  /** Time of day when the item should be processed. Defaults to Morning. */
  timeSlot: TimeSlot;
  /** Type of action to execute. Defaults to Email. */
  actionType: ScheduledActionType;
  status: ScheduledItemStatus;
  /** The date and time when the item was actually sent. Null until sent. */
  sentDate?: string; // ISO date string, null until status is Sent
  /** Whether this item was auto-scheduled by the system (vs. manually by the user). */
  isAutoScheduled?: boolean;
  /** The scheduled card for this item (1:1 relationship). Null if no card assigned. */
  scheduledCard?: ScheduledCard;
  /** The flower pre-order for this item (1:1 relationship). Null for non-flower items. */
  flowerOrder?: FlowerOrder;
  /** The mailed-card pre-order for this item (1:1 relationship). Null for non-mailed-card items. */
  mailedCardOrder?: MailedCardOrder | null;
  /** The gift pre-order for this item (1:1 relationship). Null for non-gift items. */
  giftOrder?: GiftOrder | null;
}

/**
 * Scheduled card model linking scheduled items to card templates.
 * Supports media override: if mediaToken is set, it takes precedence over the template's media.
 */
export interface ScheduledCard {
  id: number;
  scheduledItemId: number;
  scheduledItem?: ScheduledItem;
  cardTemplateId: number;
  cardTemplate?: CardTemplate;
  customMessage?: string;
  /**
   * Custom email subject line that overrides the template's default.
   * Supports placeholders: {{RECIPIENT_NAME}}, {{RECIPIENT_FIRST_NAME}}, {{SENDER_NAME}}
   * When null/empty, falls back to template's defaultSubjectLine or hardcoded default.
   */
  customSubjectLine?: string;
  status: ScheduledCardStatus;
  /**
   * Animated billing snapshot (PBI #1246).
   * null = static / never billed; 0 = Free animated; greater than 0 = Stripe charged.
   */
  chargedAmountUsd?: number | null;
  /** Catalog list amount at billing time; null for static and legacy rows. */
  listAmountUsd?: number | null;
  stripePaymentIntentId?: string | null;
  refundedAmountUsd?: number | null;
  stripeReceiptUrl?: string | null;
  /** 
   * Optional card-specific media token that overrides the template media.
   * When set, use /api/media/{mediaToken} to access the card's custom media.
   */
  mediaToken?: string;
  /**
   * Type of card-specific media: "image" or "video".
   * Only set when the card has custom media (mediaToken is set).
   */
  mediaType?: string;
  /** Frame for card-level override media (PBI #1161); entity may also send mediaFrameJson string */
  mediaFrame?: import('./mediaFrame').MediaFrame | null;
  mediaFrameJson?: string | null;
  /**
   * Unique token (GUID) for public card viewing by recipients.
   * Generated when the card is sent, used in the recipient's view URL: /card/{viewToken}
   * Only available for cards that have been sent.
   */
  viewToken?: string;
  /**
   * When the recipient confirmed open via the public card SPA (PBI #1160).
   * Null until confirm-open succeeds.
   */
  firstOpenedAt?: string | null;
  /**
   * Whether open tracking applies to this card.
   * False for legacy cards sent before open tracking shipped.
   */
  openTrackingEnabled?: boolean;
  /**
   * When the sender dismissed Overview / Contacts unopened warnings for this card.
   */
  unopenedWarningDismissedAt?: string | null;
}

/**
 * User model for storing app-specific user data.
 */
export interface User {
  id: string;
  email: string;
  name?: string;
  createdAt: string;
  updatedAt?: string;
}

/**
 * Dashboard statistics model for displaying user summary data.
 */
export interface DashboardStats {
  totalContacts: number;
  totalEventTypes: number;
  upcomingEventsCount: number;
  pendingScheduledItems: number;
}

/**
 * Upcoming event with calculated next occurrence date.
 * Used for displaying events in the dashboard widget.
 */
export interface UpcomingEvent {
  contactId: number;
  contactName: string;
  contactFirstName?: string;
  contactLastName?: string;
  eventTypeId: number;
  eventTypeName: string;
  nextOccurrence: string; // ISO date string
  daysUntil: number;
}

/**
 * Request payload for creating a new contact.
 */
export interface CreateContactRequest {
  firstName: string;
  lastName: string;
  email?: string;
  phone?: string;
  notes?: string;
  birthday?: string; // ISO date string
  /** When false, only month/day are sent; API stores sentinel year. */
  birthdayYearKnown?: boolean;
  customDates?: ContactCustomDate[];
  eventTypeIds?: number[];
  /** Street address line 1. */
  addressLine1?: string;
  /** Street address line 2 (apartment, suite, etc.). */
  addressLine2?: string;
  /** City name. */
  city?: string;
  /** State or province. */
  state?: string;
  /** Postal / ZIP code. */
  postalCode?: string;
  /** Country name. */
  country?: string;
  /** Contact's relationship to the user (e.g., "Friend", "Colleague"). */
  relationship?: string;
  /** Preferred card tone (e.g., "Professional", "Funny", "Teasing"). */
  preferredTone?: string;
  /** Preferred media type: "Image" or "Animated". */
  preferredMediaType?: string;
  /** Preferred visual art style (e.g., "Photorealistic", "Watercolor"). */
  preferredVisualStyle?: string;
  /** Default AI writing instructions for this contact. */
  aiInstructions?: string;
  /** Guest post-sign-up handoff idempotency key (#1493). */
  guestHandoffId?: string;
  /** When true, create proceeds despite duplicate email after user confirms (#1395). */
  confirmDuplicateEmail?: boolean;
}

/**
 * Request payload for updating a contact.
 */
export interface UpdateContactRequest {
  firstName: string;
  lastName: string;
  email?: string;
  phone?: string;
  notes?: string;
  birthday?: string; // ISO date string
  /** null/undefined = leave unchanged; avoids wiping year-known on partial updates. */
  birthdayYearKnown?: boolean;
  customDates?: ContactCustomDate[];
  eventTypeIds?: number[];
  /** Street address line 1. */
  addressLine1?: string;
  /** Street address line 2 (apartment, suite, etc.). */
  addressLine2?: string;
  /** City name. */
  city?: string;
  /** State or province. */
  state?: string;
  /** Postal / ZIP code. */
  postalCode?: string;
  /** Country name. */
  country?: string;
  /** Contact's relationship to the user (e.g., "Friend", "Colleague"). */
  relationship?: string;
  /** Preferred card tone (e.g., "Professional", "Funny", "Teasing"). */
  preferredTone?: string;
  /** Preferred media type: "Image" or "Animated". */
  preferredMediaType?: string;
  /** Preferred visual art style (e.g., "Photorealistic", "Watercolor"). */
  preferredVisualStyle?: string;
  /** Default AI writing instructions for this contact. */
  aiInstructions?: string;
}


/**
 * A structured address suggestion returned by the address autocomplete proxy.
 * Used by the AddressAutocomplete component to populate contact address fields.
 */
export interface AddressSuggestion {
  /** Display label for the dropdown (e.g. "123 Main St, Springfield, IL 62701, United States") */
  label: string;
  /** Street address (maps to addressLine1) */
  addressLine1: string;
  /** City / locality */
  city: string;
  /** State / province abbreviation */
  state: string;
  /** Postal / ZIP code */
  postalCode: string;
  /** ISO 3166-1 alpha-2 country code (e.g. "US") */
  country: string;
}

/**
 * Request payload for creating a new event type.
 */
export interface CreateEventTypeRequest {
  name: string;
  description?: string;
  recurrenceType: RecurrenceType;
  recurrenceInterval: number;
  dateFieldName: string;
  /** UI color for this event type (Mantine color name). Defaults to "violet" if not specified. */
  color?: string;
  dateSourceType?: DateSourceType;
  fixedMonth?: number;
  fixedDay?: number;
  floatingHolidayRule?: FloatingHolidayRule;
  floatingRuleMonth?: number;
  floatingRuleWeekday?: number;
  floatingRuleOrdinal?: number;
  floatingRuleEasterOffset?: number;
  holidayRegionLabel?: string;
}

/**
 * Request payload for updating an event type.
 */
export interface UpdateEventTypeRequest {
  name: string;
  description?: string;
  recurrenceType: RecurrenceType;
  recurrenceInterval: number;
  dateFieldName: string;
  /** UI color for this event type (Mantine color name). */
  color?: string;
  dateSourceType?: DateSourceType;
  fixedMonth?: number;
  fixedDay?: number;
  floatingHolidayRule?: FloatingHolidayRule;
  floatingRuleMonth?: number;
  floatingRuleWeekday?: number;
  floatingRuleOrdinal?: number;
  floatingRuleEasterOffset?: number;
  holidayRegionLabel?: string;
  /** Whether the seasonal marketing banner is enabled (FixedDate / FloatingHoliday only). */
  seasonalBannerEnabled?: boolean;
  /** Days before the event the banner may appear (1-60). */
  seasonalBannerLeadDays?: number;
  /** Overlay headline (max 100). */
  seasonalBannerHeadline?: string | null;
  /** Optional supporting subtext (max 160). */
  seasonalBannerSubtext?: string | null;
}

/**
 * Floating holiday preset from the curated catalog.
 * Returned by GET /api/admin/floating-holidays.
 */
export interface FloatingHolidayPresetResponse {
  name: string;
  regionLabel?: string;
  displayName: string;
  description: string;
  rule: FloatingHolidayRule;
  month?: number;
  weekday?: number;
  ordinal?: number;
  easterOffset?: number;
  nextOccurrence: string;
  color: string;
}

/**
 * Request payload for creating a scheduled item.
 */
export interface CreateScheduledItemRequest {
  contactId: number;
  eventTypeId: number;
  /** Date when the item should be sent (time is determined by timeSlot) */
  scheduledDate: string; // ISO date string
  /** Time of day when the item should be processed. Defaults to Morning if not specified. */
  timeSlot?: TimeSlot;
  cardTemplateId: number;
  customMessage?: string;
  /** Custom email subject line (overrides template default) */
  customSubjectLine?: string;
  /** When true, skips the duplicate-schedule check so the user can intentionally create a second card. */
  forceSchedule?: boolean;
}

/**
 * Common fields surfaced for any service-specific duplicate.
 * Mirrors the backend `DuplicateScheduleResponse` DTO base shape.
 */
interface DuplicateScheduleBase {
  existingScheduledItemId: number;
  /** ISO 8601 timestamp of the conflicting item. */
  existingScheduledDate: string;
}

/** Card duplicate variant — narrowed via `serviceType === ServiceType.ECard`. */
export interface CardDuplicateInfo extends DuplicateScheduleBase {
  serviceType: ServiceType.ECard;
  existingScheduledCardId?: number;
  existingTemplateName?: string;
}

/** Flower duplicate variant — narrowed via `serviceType === ServiceType.Flowers`. */
export interface FlowerDuplicateInfo extends DuplicateScheduleBase {
  serviceType: ServiceType.Flowers;
  existingFlowerOrderId?: number;
  existingFlowerProductName?: string;
  existingFlowerFulfillmentStatus?: FulfillmentStatus;
  existingFlowerPaymentStatus?: PaymentStatus;
}

/** Mailed card duplicate variant — narrowed via `serviceType === ServiceType.MailedCard`. */
export interface MailedCardDuplicateInfo extends DuplicateScheduleBase {
  serviceType: ServiceType.MailedCard;
  existingMailedCardOrderId?: number;
  existingMailedCardArtworkName?: string;
  existingMailedCardFulfillmentStatus?: FulfillmentStatus;
  existingMailedCardPaymentStatus?: PaymentStatus;
}

/** Gift duplicate variant — narrowed via `serviceType === ServiceType.Gift`. */
export interface GiftDuplicateInfo extends DuplicateScheduleBase {
  serviceType: ServiceType.Gift;
  existingGiftOrderId?: number;
  existingGiftProductName?: string;
  existingGiftFulfillmentStatus?: FulfillmentStatus;
  existingGiftPaymentStatus?: PaymentStatus;
}

/**
 * Discriminated union returned by the unified duplicate-detection pathway.
 *
 * Use the `serviceType` discriminator to access service-specific fields
 * safely. Returned by:
 *   - `GET /scheduleditems/check-duplicate?serviceType=…` (200 body)
 *   - Service create endpoints (409 body) when a duplicate is rejected server-side
 */
export type DuplicateScheduleInfo =
  | CardDuplicateInfo
  | FlowerDuplicateInfo
  | MailedCardDuplicateInfo
  | GiftDuplicateInfo;

/**
 * Request payload for creating a user-owned card template.
 */
export interface CreateCardTemplateRequest {
  /** At least one active catalog event type slug (EVT-NNN); required by the API. */
  eventTypeSlugs: string[];
  name: string;
  /** Human-readable description (max 160 chars) */
  description?: string;
  defaultGreetingText: string;
  /** Default email subject line (supports placeholders) */
  defaultSubjectLine?: string;
  isAnimated: boolean;
  previewImageUrl?: string;
}

/**
 * Request payload for updating a user-owned card template.
 * All fields are optional - only provided fields will be updated.
 */
export interface UpdateCardTemplateRequest {
  /** When set, replaces template–event-type mappings (must remain non-empty). */
  eventTypeSlugs?: string[];
  /** When set, replaces TAG-NNN assignments (system templates / admin PUT). */
  tagSlugs?: string[];
  /** Landing carousel membership for system templates (admin PUT). */
  isOnLandingCarousel?: boolean;
  name?: string;
  /** Human-readable description (max 160 chars) */
  description?: string;
  defaultGreetingText?: string;
  /** Default email subject line (supports placeholders) */
  defaultSubjectLine?: string;
  isAnimated?: boolean;
  previewImageUrl?: string;
  tone?: string | null;
  primaryPersona?: string | null;
  visualStyleName?: string | null;
}

/**
 * Information about an available template placeholder.
 * Used to display help text showing what placeholders users can use.
 */
export interface PlaceholderInfo {
  name: string;
  description: string;
  source: string;
  example: string;
}

/**
 * Card view data for public card viewing by recipients.
 * Contains only the information needed to display the card - no sensitive data.
 * 
 * Used by the public card view page (/card/:viewToken) which is accessible
 * without authentication.
 */
export interface CardViewData {
  /** The type of event (e.g., "Birthday", "Anniversary") */
  eventTypeName: string;
  /** The name of the card template used */
  templateName: string;
  /** The personalized message content with placeholders replaced */
  message: string;
  /** URL to the card's media asset (e.g., /api/media/{mediaToken}), null if no media */
  mediaUrl?: string;
  /** Type of media: "image" or "video", null if no media */
  mediaType?: string;
  /** Non-destructive 16:9 frame when custom media was framed (PBI #1161) */
  mediaFrame?: import('./mediaFrame').MediaFrame | null;
  /** The name of the person who sent the card */
  senderName: string;
  /** Processed inbox subject for this card */
  emailSubjectLine?: string;
  /** Browser / Open Graph title */
  pageTitle?: string;
  /** Absolute URL for link preview image */
  shareImageUrl?: string;
}

/**
 * Flower pre-order data matching backend FlowerOrderDto.
 * Contains all fields needed to display and manage a pre-order.
 */
export interface OrderMarketingSnapshot {
  transactionId: string;
  revenueUsd: number;
  merchantItemId?: string | null;
  itemPriceUsd: number;
  currency: string;
}

/**
 * Foreground fulfillment metadata on order create/update/place (PBI #1245).
 * Mirrors backend FulfillmentOutcomeDto / FE constants/fulfillmentOutcomes.
 */
export interface OrderFulfillmentOutcome {
  fulfillmentOutcome: string;
  fulfillmentStatus?: string;
  paymentStatus?: string;
  canRetry: boolean;
  maxRetryDays: number;
  message?: string | null;
  errorCode?: string | null;
  /** yyyy-MM-dd alternate when errorCode is FLOWER_ORDER_DATE_UNAVAILABLE (#1412). */
  suggestedDate?: string | null;
  /** Vendor cannot meet the requested date — Change date / Cancel CTAs. */
  dateUnreachable?: boolean;
}

/**
 * 409 body when foreground e-card send fails but the scheduled item was kept (PBI #1245).
 */
export interface EcardFulfillmentConflictResponse {
  message: string;
  fulfillment: OrderFulfillmentOutcome;
  scheduledItemId: number;
  scheduledCardId: number;
}

export interface FlowerOrder {
  id: number;
  contactId: number;
  eventTypeId: number;
  scheduledItemId?: number | null;
  recipientName: string;
  addressLine1?: string | null;
  addressLine2?: string | null;
  city?: string | null;
  state?: string | null;
  postalCode?: string | null;
  country?: string | null;
  /** Recipient phone number for delivery coordination. */
  phone?: string | null;
  /** Sender (customer) phone number for flower delivery partner communication. */
  senderPhone?: string | null;
  /**
   * Sender (CUSTOMER) address snapshot captured at order creation time
   * from the user's My Account home address. Maps to the vendor sender block
   * when the order is placed.
   */
  senderAddressLine1?: string | null;
  senderAddressLine2?: string | null;
  senderCity?: string | null;
  senderState?: string | null;
  senderPostalCode?: string | null;
  /** Sender ISO 3166-1 alpha-2 country code. */
  senderCountry?: string | null;
  /** Resolved card message printed on the physical card with the flowers. */
  cardMessage?: string | null;
  sku: string;
  /** Opaque catalog product id snapshotted at selection (preferred client key). */
  productPublicId?: string | null;
  productName: string;
  /** Product image URL from the catalog. */
  imageUrl?: string | null;
  /**
   * Product amount persisted on the order (catalog or accepted live quote).
   * Tax and delivery fee are separate nullable columns populated at create
   * from Florist One GetTotal (and refreshed on later re-price).
   */
  price: number;
  /** Florist One delivery fee in USD when quoted at create / update. */
  deliveryFee?: number | null;
  /** Florist One tax in USD when quoted at create / update. */
  tax?: number | null;
  /** Combined Scheduled Smiles promotion credit (PBI #1234). */
  promotionCombinedCreditUsd?: number | null;
  /** Public promotion headline snapshotted at create. */
  promotionPublicHeadline?: string | null;
  /** Snapshotted discount percent (e.g. 10). */
  promotionDiscountPercent?: number | null;
  /** Product discount portion of promotion credit. */
  promotionProductDiscountUsd?: number | null;
  /** Tax relief portion of promotion credit. */
  promotionTaxReliefUsd?: number | null;
  /** Customer product after product discount. */
  customerProductPriceUsd?: number | null;
  /** Stripe charge total when a promotion is snapshotted. */
  customerTotalUsd?: number | null;
  deliveryDate: string;
  /** Original delivery date before the vendor adjusted it. Null if never adjusted. */
  originalDeliveryDate?: string | null;
  /** Special delivery instructions for the local florist (max 100 chars). */
  specialInstructions?: string | null;
  /** Whether minor flower substitutions are allowed. Defaults to true. */
  allowSubstitutions: boolean;
  fulfillmentStatus: FulfillmentStatus;
  paymentStatus: PaymentStatus;
  cancellationAcknowledged: boolean;
  isAutoScheduled: boolean;
  /** Carrier tracking number when the vendor supports delivery tracking. */
  trackingNumber?: string | null;
  /** Direct tracking URL when provided by the vendor/carrier. */
  trackingUrl?: string | null;
  /** Carrier name when provided by the vendor. */
  carrier?: string | null;
  createdAt: string;
  updatedAt: string;
  /** Public GMV marketing snapshot returned on create only (PBI #1204). */
  marketing?: OrderMarketingSnapshot | null;
  /** Foreground place/charge outcome on create/update/place (PBI #1245). */
  fulfillment?: OrderFulfillmentOutcome | null;
}

/**
 * User-authored text for a specific writable mailed-card page.
 */
export interface MailedCardMessagePage {
  page: number;
  text: string;
}

/**
 * Writable panel metadata returned for a mailed-card artwork.
 */
export interface MailedCardPagePanel {
  pageNumber: number;
  panelKind: string;
  isWritable: boolean;
}

/**
 * Tier-aware list/member/effective pricing (PBI #1186).
 */
export interface OrderPricing {
  listUsd: number;
  memberUsd: number;
  effectiveUsd: number;
  discountPercent: number;
  audience: 'public' | 'free' | 'member';
  giftCardAmountUsd?: number | null;
  giftCardFulfillmentFeeUsd?: number;
}

/**
 * Artwork row for the mailed-card browse catalog.
 */
export interface MailedCardArtworkBrowse {
  id: number;
  slug: string;
  name: string;
  description: string;
  mediaPages: number;
  thumbnailUrl?: string | null;
  /** Effective price for the current caller. */
  priceUsd: number;
  pricing?: OrderPricing;
  eventTypeId?: number | null;
  eventTypeName?: string | null;
  eventTypeColor?: string | null;
}

/**
 * Artwork detail with writable page layout for order authoring.
 */
export interface MailedCardArtworkDetail {
  id: number;
  slug: string;
  name: string;
  description: string;
  /** Locked Auto Send sentence (public PDP GET-by-key only). */
  autopilotCopy?: string | null;
  /** Composed GMC / JSON-LD description (public PDP GET-by-key only). */
  merchantDescription?: string | null;
  mediaPages: number;
  previewImageUrls: Array<string | null>;
  priceUsd: number;
  pricing?: OrderPricing;
  eventTypeId?: number | null;
  pageLayout: MailedCardPagePanel[];
  /** Google Merchant Center offer id when the artwork is feed-published. */
  merchantItemId?: string | null;
  /** Return policy and delivery windows for Product JSON-LD (public PDP only). */
  listingPolicy?: import('./merchantListingPolicy').PublicMerchantListingPolicy | null;
  /** UTC instant when the current list price last became authoritative (public PDP only). */
  offerValidFromUtc?: string | null;
  /** Cardly media UUID — scopes gift-card tile amounts in the order wizard. */
  mediaExternalId?: string;
}

/**
 * Paginated mailed-card catalog response.
 */
export interface MailedCardArtworkCatalogPage {
  artworks: MailedCardArtworkBrowse[];
  page: number;
  pageSize: number;
  totalCount: number;
  totalPages: number;
  hasMore: boolean;
}

/**
 * Customer-selectable gift-card face value (PBI #990).
 * Matches API {@link MailedCardGiftCardOptionDto} — offered amounts only.
 */
export interface MailedCardGiftCardOption {
  amountUsd: number;
  templateExternalId: string;
  currency: string;
  /** Cardly media id — offered gift-card SKUs are per portrait/landscape media (Bug #1512). */
  mediaExternalId: string;
  mediaFormatLabel?: string;
}

/**
 * Non-sensitive mailed-card order configuration for modal defaults.
 */
export interface MailedCardConfigResponse {
  supportEmail: string;
  placementLeadDays: number;
  /** Slack buffer after Cardly max estimate for foreground placement (PBI #1369). */
  foregroundSlackBufferDays?: number;
  foregroundEstimateHorizonDays?: number;
  minRequestedArrival: string;
  shippingMethods: Array<{ code: string; label: string }>;
  supportedCountries: string[];
  maxMessageLength: number;
  /** Offered gift-card amounts, ascending; empty when none are configured. */
  giftCardOptions: MailedCardGiftCardOption[];
  /** Default tier-aware card pricing for order-modal copy (PBI #1186). */
  defaultCardPricing?: OrderPricing;
  /** Cancellation paragraph from config, shared with the assistant review summary. */
  cancellationPolicy?: string;
}

/** Tax quote request for mailed-card review (PBI #1515). */
export interface MailedCardTaxQuoteRequest {
  mailedCardArtworkId: number;
  giftCardAmountUsd?: number | null;
  addressLine1?: string;
  city?: string;
  state?: string;
  postalCode?: string;
  country?: string;
  draftScopeKey?: string | null;
  /** Requested arrival (yyyy-MM-dd); drives estimated vs sales tax label on the server. */
  requestedArrival?: string | null;
  /** Cardly preview delivery window — same inputs as foregroundPlacementDue on review. */
  cardlyEstimatedMinArrival?: string | null;
  cardlyEstimatedMaxArrival?: string | null;
}

/** Tax quote response for mailed-card review (PBI #1515). */
export interface MailedCardTaxQuoteResponse {
  preTaxSubtotalUsd: number;
  taxUsd?: number | null;
  totalUsd: number;
  stripeTaxCalculationId?: string | null;
  taxAddressSource: string;
  isEstimated: boolean;
  taxUnavailable: boolean;
  invalidAddress: boolean;
  errorMessage?: string | null;
}

/** Tax quote response for animated e-card schedule/review (PBI #1515). */
export interface AnimatedCardChargePreviewRequest {
  contactId: number;
  draftScopeKey?: string | null;
}

export interface AnimatedCardChargePreviewResponse {
  success: boolean;
  paymentMethodRequired: boolean;
  preTaxUsd: number;
  taxUsd?: number | null;
  totalUsd: number;
  listUsd?: number | null;
  stripeTaxCalculationId?: string | null;
  taxAddressSource?: string | null;
  needsBuyerAddress: boolean;
  taxUnavailable: boolean;
  invalidTaxAddress: boolean;
  taxAddressFieldError?: string | null;
  errorMessage?: string | null;
}

/**
 * Request payload for mailed-card preview generation.
 */
export interface MailedCardPreviewRequest {
  mailedCardArtworkId: number;
  message: string;
  shippingMethod: string;
  requestedArrival: string;
  /** Selected offered gift-card face value in USD, or omitted for no gift card (PBI #990). */
  giftCardAmountUsd?: number | null;
  /**
   * Cardly template UUID already chosen for an existing order. When set, preview uses this
   * snapshot instead of reconstructing from giftCardAmountUsd.
   */
  cardlyTemplateExternalId?: string | null;
  recipientFirstName?: string;
  recipientLastName?: string;
  addressLine1?: string;
  addressLine2?: string;
  city?: string;
  state?: string;
  postalCode?: string;
  country?: string;
  senderFirstName?: string;
  senderLastName?: string;
  senderAddressLine1?: string;
  senderAddressLine2?: string;
  senderCity?: string;
  senderState?: string;
  senderPostalCode?: string;
  senderCountry?: string;
}

/**
 * Preview response for mailed-card proof rendering.
 */
export type MailedCardArrivalRiskTier = 'Unknown' | 'OnTrack' | 'Uncertain' | 'TooEarly';

export interface MailedCardPreviewResponse {
  priceUsd: number;
  pricing?: OrderPricing;
  giftCardAmountUsd?: number | null;
  giftCardFulfillmentFeeUsd?: number;
  proofImageUrls: string[];
  pagePreviewUrls: Array<string | null>;
  dispatchDate?: string | null;
  estimatedMinArrival?: string | null;
  estimatedMaxArrival?: string | null;
  effectiveMinimumArrival?: string | null;
  requiresArrivalConfirmation?: boolean;
  arrivalFeasible?: boolean | null;
  arrivalRiskTier?: MailedCardArrivalRiskTier;
  foregroundPlacementDue?: boolean;
}

/**
 * Public mailed-card artwork row used by landing/public pages.
 */
export interface PublicMailedCardArtwork {
  id: number;
  slug: string;
  name: string;
  thumbnailUrl?: string | null;
  priceUsd: number;
  pricing?: OrderPricing;
  eventTypeColor?: string | null;
  eventTypeId?: number | null;
}

/**
 * Gift pre-order DTO returned by the API (Goody pipeline).
 */
export interface GiftOrder {
  id: number;
  contactId: number;
  eventTypeId: number;
  scheduledItemId?: number | null;
  giftProductId: number;
  requestedDate: string;
  recipientFirstName: string;
  recipientLastName: string;
  fulfillmentStatus: FulfillmentStatus;
  paymentStatus: PaymentStatus;
}

/**
 * Mailed-card pre-order DTO returned by the API.
 */
export interface MailedCardOrder {
  id: number;
  contactId: number;
  eventTypeId: number;
  scheduledItemId?: number | null;
  mailedCardArtworkId: number;
  artworkSlug: string;
  artworkName: string;
  artworkThumbnailUrl?: string | null;
  message: string;
  shippingMethod: string;
  requestedArrival: string;
  priceUsd: number;
  quotedTaxUsd?: number | null;
  chargedTaxUsd?: number | null;
  /** Server-owned; same rule as confirmation email (MailedCardOrderTaxDisplay). */
  taxIsEstimated?: boolean;
  totalUsd?: number | null;
  /** Optional digital gift-card face value in USD (PBI #990). */
  giftCardAmountUsd?: number | null;
  giftCardFulfillmentFeeUsd?: number;
  cardPriceUsd?: number;
  /** Cardly template UUID resolved at create/update — prefer for existing-order proof preview. */
  cardlyTemplateExternalId?: string | null;
  fulfillmentStatus: FulfillmentStatus;
  paymentStatus: PaymentStatus;
  cancellationAcknowledged: boolean;
  isAutoScheduled: boolean;
  cardlyOrderId?: string | null;
  recipientFirstName: string;
  recipientLastName: string;
  addressLine1?: string | null;
  addressLine2?: string | null;
  city?: string | null;
  state?: string | null;
  postalCode?: string | null;
  country?: string | null;
  senderFirstName: string;
  senderLastName: string;
  senderAddressLine1?: string | null;
  senderAddressLine2?: string | null;
  senderCity?: string | null;
  senderState?: string | null;
  senderPostalCode?: string | null;
  senderCountry?: string | null;
  rowVersion: string;
  createdAt: string;
  updatedAt: string;
  /** Public GMV marketing snapshot returned on create only (PBI #1204). */
  marketing?: OrderMarketingSnapshot | null;
  /** Foreground place/charge outcome on create/update/place (PBI #1245). */
  fulfillment?: OrderFulfillmentOutcome | null;
}

/**
 * Optional recipient fields passed as query parameters on the authenticated
 * delivery-fee-estimate GET so the backend can forward a fuller Florist One
 * RECIPIENT block (defensive; vendor pricing still evaluates ZIP only).
 */
export interface DeliveryAddressForFeeEstimate {
  recipientName?: string;
  addressLine1?: string;
  city?: string;
  state?: string;
}

/**
 * Delivery fee estimation result from the backend.
 * Wraps Florist One checkdeliverydate + gettotal into explicit UI states.
 */
export interface DeliveryFeeEstimate {
  /**
   * "success" — fee available; "unserviceable" — ZIP not served (Pass A empty dates);
   * "available-fee-unknown" — ZIP is served but gettotal could not price (catalog drift / vendor message);
   * "api-error" — transport failure or we could not build a quote at all.
   */
  state: 'success' | 'unserviceable' | 'available-fee-unknown' | 'api-error';
  /** Delivery fee in USD. Present only when state is "success"; null otherwise. */
  deliveryFee: number | null;
  /** Normalized zip code echoed back for display in warning messages. */
  zipCode: string;
}

/**
 * Non-sensitive Florist One display configuration.
 * Used by FlowerCancellationNotice and notification emails.
 */
export interface FlowerConfigResponse {
  customerServicePhone: string;
  cancellationPolicy?: string;
  customerServiceText: string;
  customerServiceUrl: string;
  customerServiceHours: string;
  /** Maximum price for the Budget tier (e.g. 49.99). */
  budgetMaxPrice: number;
  /** Maximum price for the Standard tier (e.g. 79.99). */
  standardMaxPrice: number;
  /**
   * Days before delivery when flower placement runs (PBI #1245).
   * Used to show the Review / Place order step inside the window.
   */
  placementDaysBefore: number;
}
